import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Avatar } from "@/ui/Avatar";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody } from "@/ui/Card";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { ProgressBar } from "@/ui/ProgressBar";
import { EmptyState } from "@/ui/feedback/states";
import { formatDZD } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { useUpdateTaskStatus } from "@/features/tasks/tasks.hooks";
import type { Task, TaskStatus, TeamMember } from "@/types";

export function MemberProfileView({
  member,
  tasks,
  onBack,
  canUpdateTasks = false,
}: {
  member: TeamMember;
  tasks: Task[];
  onBack?: () => void;
  canUpdateTasks?: boolean;
}) {
  const { t } = useTranslation();
  const { TEAM_ROLE, TASK_STATUS, PRIORITY } = useLabels();
  const updateStatus = useUpdateTaskStatus();

  const onStatus = async (task: Task, status: TaskStatus) => {
    try {
      await updateStatus.mutateAsync({ id: task.id, status });
      toast.success(t("tasks.statusUpdated"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("tasks.updateStatusFailed")));
    }
  };

  const taskColumns: Column<Task>[] = [
    { key: "title", header: t("tasks.columns.task"), cell: (task) => <span className="font-semibold text-text">{task.title}</span> },
    { key: "client", header: t("tasks.columns.client"), cell: (task) => task.clientName ? <Badge tone="info">{task.clientName}</Badge> : <span className="text-text3">-</span> },
    {
      key: "priority",
      header: t("tasks.columns.priority"),
      cell: (task) => (
        <Badge tone={task.priority === "HIGH" ? "danger" : task.priority === "MEDIUM" ? "warning" : "default"} dot>
          {PRIORITY[task.priority].label}
        </Badge>
      ),
    },
    { key: "status", header: t("tasks.columns.status"), cell: (task) => <Badge tone={TASK_STATUS[task.status].tone} dot>{TASK_STATUS[task.status].label}</Badge> },
    { key: "due", header: t("tasks.columns.due"), cell: (task) => <span className="text-text2">{task.dueDate ?? "-"}</span> },
    { key: "progress", header: t("tasks.columns.progress"), cell: (task) => <div className="w-24"><ProgressBar value={task.progress} /></div> },
    ...(canUpdateTasks
      ? [{
          key: "actions" as const,
          header: t("tasks.columns.updateStatus"),
          align: "right" as const,
          cell: (task: Task) => (
            <div className="flex items-center gap-1">
              {task.status !== "IN_PROGRESS" && task.status !== "DONE" && (
                <Button variant="ghost" size="xs" onClick={() => onStatus(task, "IN_PROGRESS")}>{t("tasks.markInProgress")}</Button>
              )}
              {task.status !== "ON_HOLD" && task.status !== "DONE" && (
                <Button variant="ghost" size="xs" className="text-warning" onClick={() => onStatus(task, "ON_HOLD")}>{t("tasks.markBlocked")}</Button>
              )}
              {task.status !== "DONE" && (
                <Button variant="ghost" size="xs" className="text-success" onClick={() => onStatus(task, "DONE")}>{t("tasks.markDone")}</Button>
              )}
            </div>
          ),
        }]
      : []),
  ];

  const infoRows = [
    [t("team.name"), member.name],
    [t("team.email"), member.email],
    [t("team.phone"), member.phone ?? "-"],
    [t("team.role"), TEAM_ROLE[member.role].label],
    [t("team.statusLabel"), member.status === "ACTIVE" ? t("team.status.active") : member.status === "ON_LEAVE" ? t("team.status.onLeave") : t("team.status.inactive")],
    [t("team.compensationType"), t(`team.compensation.${member.compensation}`)],
    [t("team.salaryBase"), formatDZD(member.salary)],
  ];

  return (
    <>
      {onBack && (
        <Button variant="ghost" size="sm" className="mb-3" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" /> {t("team.backToTeam")}
        </Button>
      )}

      <Card className="mb-5">
        <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <Avatar name={member.name} size={56} className="rounded-lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-display text-[24px] font-bold text-text">{member.name}</h1>
              <Badge tone={TEAM_ROLE[member.role].tone}>{TEAM_ROLE[member.role].label}</Badge>
              <Badge tone={member.status === "ACTIVE" ? "success" : member.status === "ON_LEAVE" ? "warning" : "danger"} dot>
                {member.status === "ACTIVE" ? t("team.status.active") : member.status === "ON_LEAVE" ? t("team.status.onLeave") : t("team.status.inactive")}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-text2">{member.email}</p>
          </div>
          <Badge tone={member.account?.isActive ? "success" : member.account ? "warning" : "default"}>
            {member.account ? (member.account.isActive ? t("team.accountActive") : t("team.accountInactive")) : t("team.noLinkedAccount")}
          </Badge>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-3.5">
        <div className="space-y-3.5">
          <Card>
            <CardBody>
              <div className="text-[13px] font-bold text-text mb-3.5">{t("team.profile")}</div>
              {infoRows.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-3 py-2.5 border-b border-border last:border-0">
                  <span className="text-xs text-text2">{label}</span>
                  <span className="text-[12.5px] font-semibold text-text text-right">{value}</span>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="text-[13px] font-bold text-text mb-3.5">{t("team.linkedAccount")}</div>
              <div className="space-y-2.5 text-[12.5px]">
                <div className="flex justify-between gap-3">
                  <span className="text-text2">{t("team.email")}</span>
                  <span className="text-text text-right">{member.account?.email ?? "-"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text2">{t("team.role")}</span>
                  <span className="text-text text-right">{member.account?.roles[0]?.label ?? member.account?.roles[0]?.name ?? "-"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text2">{t("team.accountStatus")}</span>
                  <span className="text-text text-right">{member.account ? (member.account.isActive ? t("team.accountActive") : t("team.accountInactive")) : t("team.noLinkedAccount")}</span>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="text-[13px] font-bold text-text mb-3.5">{t("team.assignedClients")}</div>
              {member.assignedClients?.length ? (
                <div className="space-y-2">
                  {member.assignedClients.map((client) => (
                    <div key={client.id} className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-0">
                      <span className="text-[12.5px] font-semibold text-text">{client.name}</span>
                      <Badge tone="info">{client.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title={t("team.noAssignedClients")} />
              )}
            </CardBody>
          </Card>
        </div>

        <Card>
          <DataTable columns={taskColumns} rows={tasks} rowKey={(task) => task.id} empty={{ title: t("tasks.myTasksEmpty") }} />
        </Card>
      </div>
    </>
  );
}
